import React, {useEffect, useMemo, useState} from 'react';
import {
  Box,
  CircularProgress,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import {logError} from '../../shared/services/logger.service';
import {useToolbar} from '../../components/ToolbarContext';
import {
  PermissionCatalogItem,
  RbacCompanyUserDto,
  RbacGroupDto,
  RbacRoleDto,
} from '../../shared/permissions/permissions';
import {
  getRbacGroups,
  getRbacPermissions,
  getRbacRoles,
  getRbacUsers,
  setRbacRolePermissions,
} from './rbac.service';
import {getCompanyUserAssignments} from './company-user-assignment.service';
import {CompanyUserAssignmentDto} from './dtos/company-user-assignment.dto';
import {usePermissions} from '../../shared/permissions/PermissionContext';
import PersonDetailComponent, {PersonEditorTarget} from './PersonDetailComponent';
import {toE164} from '../../shared/phone';
import {
  AddButton,
  CardSpec,
  FilterSearchBox,
  ListBody,
  ListPageScroll,
  ListPageTitleRow,
  ListRow,
  useStatusList,
} from '../../shared/components/list-page/ListPageKit';
import {displayPhone, formatHourlyRate} from '../../shared/components/list-page/list-format';
import {ListWithRecord, RecordPage, useRecordRoute} from '../../shared/components/record/RecordKit';

/*
 * The Team Members card's realistic widths, measured in Chrome with the card's
 * own CSS. Shippy's Tree Service has eleven team members, too few to judge by,
 * so the name takes the clients' 98th percentile (the same kind of names)
 * and the rest the longest value each slot can show:
 *   name     224px at 18px  "Not Sure What Her Name Is"
 *   phone    115px at 14px  "No phone number"
 *   level    187px at 14px  "Level 1 (Basic Crew Member)"
 *   rate     150px at 14px  "Billable rate $188.88/hr"
 *   status    64px          the "Inactive" pill
 * Switch points (cardSwitchPoints): one row from 894px, grouped from 667px.
 */
const TEAM_CARD_SPEC: CardSpec = {
  title: 224,
  status: 64,
  slots: [
    {key: 'phone', group: 'who', width: 115},
    {key: 'level', group: 'details', width: 187},
    {key: 'rate', group: 'money', width: 150},
  ],
};

const NO_LEVEL = 'No permission level';

interface PersonRow {
  id: string;
  kind: 'user' | 'invite';
  recordId: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  status: 'Active' | 'Pending' | 'Inactive';
  accessLevel: string;
  billableHourlyRate?: number | null;
}

const formatPersonAddress = (person: {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}): string => {
  const cityState = [person.city, person.state].map(part => part?.trim()).filter(Boolean).join(', ');
  return [person.address, cityState, person.postalCode].map(part => part?.trim()).filter(Boolean).join(', ');
};

const TeamAccessPage = () => {
  const navigate = useNavigate();
  const toolbarContext = useToolbar();
  const {refresh, me} = usePermissions();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<PermissionCatalogItem[]>([]);
  const [roles, setRoles] = useState<RbacRoleDto[]>([]);
  const [groups, setGroups] = useState<RbacGroupDto[]>([]);
  const [users, setUsers] = useState<RbacCompanyUserDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [assignments, setAssignments] = useState<CompanyUserAssignmentDto[]>([]);
  // A team member opens as its own page over the list (SQ-599): /settings/access/users/3, …/invites/7, …/new.
  const record = useRecordRoute('/settings/access');
  const openRecord = record.open;
  const [loadError, setLoadError] = useState<string | null>(null);

  const groupNameById = useMemo(
    () => new Map(groups.map(group => [group.id, group.name])),
    [groups],
  );

  const pendingInvites = useMemo(() => {
    const signedInEmails = new Set(
      users.map(user => user.email?.toLowerCase()).filter((email): email is string => Boolean(email)),
    );
    const signedInPhones = new Set(
      users.map(user => toE164(user.phoneNumber)).filter((phone): phone is string => Boolean(phone)),
    );
    return assignments.filter(assignment => {
      const email = assignment.email?.trim().toLowerCase();
      if (email && signedInEmails.has(email)) {
        return false;
      }
      const phone = toE164(assignment.phoneNumber);
      if (phone && signedInPhones.has(phone)) {
        return false;
      }
      return true;
    });
  }, [assignments, users]);

  const personRows: PersonRow[] = useMemo(() => {
    const userRows = users.map(user => ({
      id: `user-${user.id}`,
      kind: 'user' as const,
      recordId: user.id,
      name: user.name,
      email: user.email ?? '',
      phone: user.phoneNumber ?? '',
      address: formatPersonAddress(user),
      status: user.isActive === false ? 'Inactive' as const : 'Active' as const,
      accessLevel: user.groupIds
        .map(groupId => groupNameById.get(groupId))
        .filter((name): name is string => Boolean(name))
        .join(', ') || NO_LEVEL,
      billableHourlyRate: user.billableHourlyRate,
    }));
    const inviteRows = pendingInvites.map(invite => ({
      id: `invite-${invite.id}`,
      kind: 'invite' as const,
      recordId: invite.id,
      name: invite.name || invite.email || invite.phoneNumber || 'Team member',
      email: invite.email ?? '',
      phone: invite.phoneNumber ?? '',
      address: formatPersonAddress(invite),
      status: invite.isActive === false ? 'Inactive' as const : 'Pending' as const,
      accessLevel: (invite.defaultGroupId != null && groupNameById.get(invite.defaultGroupId)) || NO_LEVEL,
      billableHourlyRate: invite.billableHourlyRate,
    }));
    return [...userRows, ...inviteRows].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [groupNameById, pendingInvites, users]);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [permissionRows, roleRows, groupRows, userRows, assignmentRows] = await Promise.all([
        getRbacPermissions(navigate),
        getRbacRoles(navigate),
        getRbacGroups(navigate),
        getRbacUsers(navigate),
        getCompanyUserAssignments(navigate),
      ]);
      setPermissions(permissionRows);
      setRoles(roleRows);
      setGroups(groupRows);
      setUsers(userRows);
      setAssignments(assignmentRows);
    } catch (error) {
      logError('Error loading team access', {}, error as Error);
      setLoadError(`Couldn't load the team: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    toolbarContext.initToolbar('TeamAccessPage', 'Team & access');
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedPermissions = useMemo(() => {
    const byArea = new Map<string, PermissionCatalogItem[]>();
    for (const permission of permissions) {
      const list = byArea.get(permission.featureArea) ?? [];
      list.push(permission);
      byArea.set(permission.featureArea, list);
    }
    return Array.from(byArea.entries());
  }, [permissions]);

  const handleToggleRolePermission = async (role: RbacRoleDto, code: string, enabled: boolean) => {
    const next = enabled
      ? [...role.permissionCodes, code]
      : role.permissionCodes.filter(c => c !== code);
    setSaving(true);
    try {
      const updated = await setRbacRolePermissions(navigate, role.id, next);
      setRoles(current => current.map(r => r.id === updated.id ? updated : r));
      await refresh();
    } catch (error) {
      logError('Error updating role permissions', {roleId: role.id}, error as Error);
      enqueueSnackbar(`Error updating role: ${error}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setSaving(false);
    }
  };

  // The person the URL names, once the team has loaded.
  const editor: PersonEditorTarget | null = useMemo(() => {
    if (record.splat === 'new') return {kind: 'new'};
    const [kind, id] = record.splat.split('/');
    if (kind === 'users') {
      const user = users.find(u => u.id === Number(id));
      return user ? {kind: 'user', user} : null;
    }
    if (kind === 'invites') {
      const invite = pendingInvites.find(a => a.id === Number(id));
      return invite ? {kind: 'invite', invite} : null;
    }
    return null;
  }, [record.splat, users, pendingInvites]);

  const handlePersonSaved = async () => {
    record.close();
    await load();
    await refresh();
  };

  const personCards: ListRow[] = useMemo(() => personRows.map(row => ({
    key: row.id,
    title: row.name,
    // All four lines always render, as on Android: a missing phone or level
    // says so in words, so a half-filled team member reads as half-filled.
    values: {
      phone: displayPhone(row.phone) ?? 'No phone number',
      level: row.accessLevel,
      rate: row.billableHourlyRate != null
        ? {label: 'Billable rate', value: formatHourlyRate(row.billableHourlyRate)}
        : {label: 'Billable rate', value: 'Not set', muted: true},
    },
    status: row.status,
    // Email and street address are not on the card, but the search finds them.
    searchText: [row.email, row.address].join(' '),
    onClick: () => openRecord(`${row.kind}s/${row.recordId}`),
  })), [personRows, openRecord]);
  const people = useStatusList(personCards, []);

  return (
    <ListWithRecord
      record={record.splat !== '' && (
        <RecordPage key={record.splat} name="TeamMemberPage">
          {editor ? (
            <PersonDetailComponent
              page
              target={editor}
              groups={groups}
              roles={roles}
              permissions={permissions}
              currentUserId={me?.id ?? null}
              onClose={record.close}
              onSaved={() => void handlePersonSaved()}
            />
          ) : (
            <Typography color="text.secondary" sx={{py: 3}}>
              {loading ? 'Loading…' : loadError ?? 'This team member is no longer on the team.'}
            </Typography>
          )}
        </RecordPage>
      )}
      list={<ListPageScroll>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{mb: 2, mt: -1}}>
        <Tab label="People" />
        <Tab label="Role permissions" />
      </Tabs>

      {tab === 0 && (
        <>
          <ListPageTitleRow
            title="Team Members"
            button={<AddButton label="Add New Team Member" onClick={() => record.open('NEW')}/>}
          />
          <FilterSearchBox
            label="Search"
            chips={[]}
            onChipChange={() => {}}
            query={people.query}
            onQueryChange={people.setQuery}
          />
          <ListBody
            loading={loading}
            error={loadError}
            onRetry={() => void load()}
            rows={people.shown}
            spec={TEAM_CARD_SPEC}
            query={people.query}
          />
        </>
      )}

      {tab === 1 && loading && (
        <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
          <CircularProgress />
        </Box>
      )}

      {tab === 1 && !loading && (
        <Box sx={{overflow: 'auto'}}>
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            These are the grants for each group. People get the union of every group they belong to.
          </Typography>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Permission</TableCell>
                {roles.map(role => (
                  <TableCell key={role.id} align="center">{role.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedPermissions.map(([area, areaPermissions]) => (
                <React.Fragment key={area}>
                  <TableRow>
                    <TableCell colSpan={roles.length + 1}>
                      <Typography variant="subtitle2" sx={{textTransform: 'uppercase'}}>{area}</Typography>
                    </TableCell>
                  </TableRow>
                  {areaPermissions.map(permission => (
                    <TableRow key={permission.code}>
                      <TableCell>{permission.description}</TableCell>
                      {roles.map(role => (
                        <TableCell key={role.id} align="center">
                          <Switch
                            size="small"
                            disabled={saving}
                            checked={role.permissionCodes.includes(permission.code)}
                            onChange={(e) => void handleToggleRolePermission(role, permission.code, e.target.checked)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </ListPageScroll>}
    />
  );
};

export default TeamAccessPage;
